<template>
  <ClientOnly>
    <div class="carousel-container" :style="cssVars">
      <div
        v-if="computedArrows && showPrevArrow"
        class="carousel-arrow-prev"
        @click="handleArrowClick(10)"
      >
        <b-icon icon="chevron-left" />
      </div>
      <div
        v-if="computedArrows && showNextArrow"
        class="carousel-arrow-next"
        @click="handleArrowClick(-10)"
      >
        <b-icon icon="chevron-right" />
      </div>
      <div
        ref="content"
        class="carousel-content"
        :class="{ 'free-mode': freeMode, 'coverflow-centered': coverflowCentered && computedCoverflow }"
      >
        <div ref="items" class="carousel-items" :class="{ 'block-pointer-events': animation }" :style="`gap: ${gapValue}`">
          <div
            v-for="(item, index) in contentItems"
            ref="contentItems"
            :key="item.sort"
            :data-sort="item.sort"
            class="carousel-item"
            :class="[
              {
                active: showActive && isActiveItem(item.sort),
                'is-image': isChildImage(item),
                'coverflow-centered': coverflowCentered && getCoverflowCenteredIndex(index)
              },
            ]"
            :style="`cursor: ${cursor}`"
          >
            <RenderNode
              ref="childContentItem"
              :key="item.sort"
              :value="item.item"
            />
          </div>
        </div>
        <div v-if="$slots['overlay']" ref="overlayItem" class="overlay-item">
          <slot name="overlay" />
        </div>
      </div>
    </div>
  </ClientOnly>
</template>

<script>
// import Hammer from 'hammerjs'
import RenderNode from '../RenderNode.vue'

export default {
  name: 'Carousel',
  components: { RenderNode },
  props: {
    gap: {
      type: Number,
      default: 0
    },
    visibleItems: {
      type: [Object, Number],
      default: 1
    },
    coverflow: {
      type: [Object, Boolean],
      default: false
    },
    increment: {
      type: Number,
      default: 0
    },
    acceleration: {
      type: Boolean,
      default: false
    },
    loop: {
      type: Boolean,
      default: false
    },
    showActive: {
      type: Boolean,
      default: false
    },
    arrows: {
      type: [Object, Boolean],
      default: null
    },
    zoom: {
      type: Boolean,
      default: false
    },
    allowKeyboard: {
      type: Boolean,
      default: false
    },
    freeMode: {
      type: Boolean,
      default: false
    },
    allowZoom: {
      type: Boolean,
      default: false
    },
    coverflowCentered: {
      type: Boolean,
      default: false
    },
    initialIndex: {
      type: Number,
      default: 0
    },
    alignItems: {
      type: String,
      default: 'center'
    }
  },
  data () {
    return {
      x: 0,
      startX: 0,
      scrollLeft: 0,
      activeItem: 1,
      activeItemIndex: 1,
      contentWidth: 0,
      itemContainerWidth: 0,
      itemsWidth: 0,
      countItems: 0,
      viewportWidth: 0,
      gapValue: 0,
      gapCssValue: '0px',
      loopValue: false,
      animation: false,
      incrementValue: 1,
      visibleItemsValue: 1,
      visibleItemsByViewport: null,
      arrowsByViewport: null,
      coverflowByViewport: null,
      firstVisibleItem: 1,
      contentItems: [],
      currentTarget: null,
      panStart: false,
      touchDetails: {
        start: null,
        end: null
      },
      mouseDetails: {
        start: null,
        end: null
      },
      listeners: [],
      grabStart: false,
      manager: null,
      overlayManager: null,
      zoomed: false,
      itemsCentered: false,
      contentSignature: ''
    }
  },
  computed: {
    cssVars () {
      return {
        '--itemContainerWidthValue': this.itemContainerWidthValue,
        '--gapCssValue': this.gapCssValue,
        '--alignItemsValue': this.alignItems,
        '--overflowValue': this.overflowValue,
        '--scrollBehaviourValue': this.scrollBehaviourValue
      }
    },
    isMobile () {
      return this.viewportWidth <= 767
    },
    isTablet () {
      return this.viewportWidth > 767 && this.viewportWidth <= 1023
    },
    isDesktop () {
      return this.viewportWidth > 1023 && this.viewportWidth < 1408
    },
    isFullhd () {
      return this.viewportWidth >= 1408
    },
    getViewportName () {
      let name = ''
      if (this.isMobile) {
        name = 'mobile'
      } else if (this.isTablet) {
        name = 'tablet'
      } else if (this.isDesktop) {
        name = 'desktop'
      } else if (this.isFullhd) {
        name = 'fullhd'
      }
      return name
    },
    computedVisibleItems () {
      return this.visibleItemsByViewport[this.getViewportName]
    },
    computedArrows () {
      return this.contentWidth <= this.itemsWidth && !this.zoomed
        ? this.arrowsByViewport[this.getViewportName]
        : false
    },
    freeModeDesktop () {
      return this.freeMode && this.isDesktop
    },
    computedCoverflow () {
      return this.coverflowByViewport[this.getViewportName]
    },
    itemContainerWidthValue () {
      return this.itemContainerWidth ? this.itemContainerWidth + 'px' : '100%'
    },
    overflowValue () {
      return this.acceleration && !this.isDesktop ? 'scroll' : 'hidden'
    },
    scrollBehaviourValue () {
      return this.freeModeDesktop ? 'auto' : 'smooth'
    },
    middleItem () {
      return Math.ceil(this.visibleItemsValue / 2)
    },
    showPrevArrow () {
      return this.x < 0
    },
    showNextArrow () {
      return this.x * (-1) + this.contentWidth + (this.computedVisibleItems - 1) * this.gapValue < this.itemsWidth
    },
    cursor () {
      return this.allowZoom && this.zoomed
        ? 'zoom-out'
        : this.allowZoom && !this.zoomed
          ? 'zoom-in'
          : !this.allowZoom && this.showActive
              ? 'pointer'
              : 'auto'
    }
  },
  watch: {
    freeModeDesktop: {
      handler (value) {
        if (this.freeMode) {
          this.$nextTick(() => {
            if (this.manager) {
              this.manager.get('pan').set({ enable: value })
            }
            if (this.$refs.content) {
              this.x = 0
              this.$refs.content.scrollLeft = 0
              this.setTranslate()
            }
          })
        }
      },
      immediate: true
    }
  },
  created () {
    this.visibleItemsByViewport = this.getVisibleItemsByViewport()
    this.arrowsByViewport = this.getArrowsByViewport()
    this.coverflowByViewport = this.getCoverflowByViewport()
    this.activeItem = this.initialIndex + 1
  },
  async mounted () {
    await this.checkRender()
    if (!this.acceleration || (this.acceleration && this.freeMode)) {
      await this.hammer()
    }
    this.addResizeListener()
    await this.updateVariables()
    if (!this.itemsCentered && this.contentWidth >= this.itemsWidth) {
      this.$refs.content.style.display = 'flex'
      this.$refs.content.style.justifyContent = 'center'
      this.itemsCentered = true
      this.manager.get('pan').set({ enable: false })
    } else if (this.itemsCentered && this.itemsWidth > this.contentWidth) {
      this.$refs.content.style.display = ''
      this.$refs.content.style.justifyContent = ''
      this.itemsCentered = false
    }
    this.addEventListeners()
    this.blockEvents()
    if (!this.freeMode) {
      document.body.style.webkitOverflowScrolling = 'touch'
      if (this.allowKeyboard) {
        window.addEventListener('keydown', this.keydown)
      }
      await this.updateState()
      if (this.loopValue) {
        this.shiftElements()
      }
    }
  },
  async updated () {
    await this.syncContentItems()
  },
  beforeUnmount () {
    if (this.freeMode) {
      window.removeEventListener('resize', this.resize)
    } else {
      window.removeEventListener('resize', this.resize)
      window.removeEventListener('keydown', this.keydown)
      this.removeEventListeners()
    }
  },
  methods: {
    checkRender (attempt = 0) {
      const hasContent = this.content().length > 0
      const hasPendingAsyncContent = (this.$slots.default || [])
        .some(x => x && x.asyncMeta && x.asyncMeta.tag === 'CarouselItem')

      if (hasContent && !hasPendingAsyncContent) {
        this.init()
        return Promise.resolve(true)
      }

      // Przy pustych slotach (np. dane z backendu jeszcze się ładują)
      // odraczamy inicjalizację, żeby nie liczyć szerokości z countItems = 0.
      if (attempt >= 200) {
        this.init()
        return Promise.resolve(false)
      }

      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(this.checkRender(attempt + 1))
        }, 16)
      })
    },
    async syncContentItems () {
      const items = this.content()
      const signature = this.getContentSignature(items)
      if (signature === this.contentSignature) {
        return
      }

      this.init(items)
      await this.updateVariables()
      if (!this.freeMode) {
        await this.updateState()
        if (this.loopValue) {
          await this.shiftElements()
        }
      }
    },
    getContentSignature (items) {
      return items
        .map(item => item.item && item.item.key !== undefined ? item.item.key : item.sort)
        .join('|')
    },
    toggleManager (value) {
      if (this.manager) {
        const delay = value ? 0 : 200
        this.zoomed = !value
        setTimeout(() => {
          this.manager.get('pan').set({ enable: value })
        }, delay)
      }
    },
    handleActiveItem (newActiveItem, emit = false) {
      if (this.activeItem !== newActiveItem) {
        this.activeItem = newActiveItem
        this.activeItemIndex =
          this.contentItems.findIndex(el => el.sort === newActiveItem) + 1
        this.updateState(emit)
      }
    },
    addResizeListener () {
      if (this.$refs.contentItems && this.$refs.contentItems.length) {
        window.addEventListener('resize', this.resize)
      } else {
        setTimeout(() => {
          this.addResizeListener()
        }, 100)
      }
    },
    addEventListeners () {
      if (this.$refs.contentItems) {
        this.$refs.contentItems.forEach((item) => {
          item.addEventListener('mousedown', this.mousedown)
          item.addEventListener('mouseup', this.mouseup)
          item.addEventListener('touchstart', this.touchstart)
          item.addEventListener('touchend', this.touchend, { passive: false })
          this.listeners.push(item)
        })
      } else {
        setTimeout(() => {
          this.addEventListeners()
        }, 100)
      }
    },
    removeEventListeners () {
      this.listeners.forEach((item) => {
        item.removeEventListener('mousedown', this.mousedown)
        item.removeEventListener('mouseup', this.mouseup)
        item.removeEventListener('touchstart', this.touchstart)
        item.removeEventListener('touchend', this.touchend)
      })
    },
    keydown ($event) {
      if ($event.keyCode === 39 && !this.zoomed) {
        // this.zoomOut()
        this.handleArrowClick(-10)
      } else if ($event.keyCode === 37 && !this.zoomed) {
        // this.zoomOut()
        this.handleArrowClick(10)
      } else if ($event.keyCode === 27) {
        this.$emit('close')
      }
    },
    mousedown ($event) {
      if (this.zoomed) {
        return
      }
      this.panStart = false
      this.mouseDetails.start = { x: $event.clientX, y: $event.clientY }
    },
    mouseup ($event) {
      if (this.panStart) {
        const closestLink = $event.target.closest('a')
        if (closestLink) {
          closestLink.addEventListener('click', this.captureLinkClick, true)
        }
      }
      if (this.zoomed) {
        return
      }
      this.mouseDetails.end = { x: $event.clientX, y: $event.clientY }
      if (
        !this.panStart &&
        this.mouseDetails &&
        this.mouseDetails.start &&
        this.mouseDetails.end &&
        Math.abs(this.mouseDetails.end.y - this.mouseDetails.start.y) < 5 &&
        Math.abs(this.mouseDetails.end.x - this.mouseDetails.start.x) < 5
      ) {
        this.currentTarget = $event.currentTarget
        this.handleClick(this.currentTarget.dataset.sort)
      }
    },
    captureLinkClick (e) {
      e.stopPropagation()
      e.preventDefault()
      const closestLink = e.target.closest('a')
      if (closestLink) {
        closestLink.removeEventListener('click', this.captureLinkClick, true)
      }
    },
    touchstart ($event) {
      if (this.zoomed) {
        return
      }
      this.panStart = false
      this.currentTarget = $event.currentTarget
      if ($event.changedTouches.length === 1) {
        this.touchDetails.start = {
          x: $event.changedTouches[0].clientX,
          y: $event.changedTouches[0].clientY
        }
      }
    },
    touchend ($event) {
      if (this.zoomed) {
        return
      }
      if ($event.cancelable) {
        $event.preventDefault()
      }
      if ($event.changedTouches.length === 1) {
        this.touchDetails.end = {
          x: $event.changedTouches[0].clientX,
          y: $event.changedTouches[0].clientY
        }
      }
      if (
        !this.panStart &&
        Math.abs(this.touchDetails.end.y - this.touchDetails.start.y) < 5 &&
        Math.abs(this.touchDetails.end.x - this.touchDetails.start.x) < 5
      ) {
        this.currentTarget = $event.currentTarget
        const clickable = $event.target.closest('a')
        if (clickable) {
          clickable.click()
        } else {
          this.handleClick(this.currentTarget.dataset.sort)
        }
      }
    },
    disableTouchScroll () {
      window.addEventListener('touchmove', this.preventScroll, { passive: false })
    },
    enableTouchScroll () {
      window.removeEventListener('touchmove', this.preventScroll)
    },
    preventScroll (e) {
      e.preventDefault()
    },
    init (items = null) {
      this.contentItems = items || this.content()
      this.countItems = this.contentItems.length
      this.contentSignature = this.getContentSignature(this.contentItems)
      this.activeItemIndex =
      this.contentItems.findIndex(x => x.sort === this.activeItem) + 1
      // Reset ilości widocznych elementów
      if (
        this.coverflow.mobile ||
      this.coverflow.tablet ||
      this.coverflowCentered
      ) {
        this.visibleItemsByViewport.mobile = 1
        this.visibleItemsByViewport.tablet = 1
        if (this.isNumber(this.visibleItems) && this.visibleItems === 1) {
          this.visibleItemsByViewport.desktop = 1
        } else {
          this.visibleItemsByViewport.desktop = 3
        }
      }
    },
    content () {
      return (this.$slots.default || [])
        .filter((x) => {
          const tag = x && x.componentOptions && x.componentOptions.tag
          const name = x && x.componentOptions && x.componentOptions.Ctor && x.componentOptions.Ctor.options && x.componentOptions.Ctor.options.name
          const typeName = x && x.type && x.type.name
          return tag === 'CarouselItem' || name === 'CarouselItem' || typeName === 'CarouselItem'
        })
        .map((item, index) => {
          return {
            sort: index + 1,
            key: index + 1,
            index,
            item,
            zoom: this.allowZoom
          }
        })
    },
    async reset () {
      this.contentItems = this.content()
      this.activeItem = 1
      this.activeItemIndex =
        this.contentItems.findIndex(x => x.sort === this.activeItem) + 1
      if (!this.freeMode) {
        await this.updateState()
      }
    },
    async resize () {
      if (this.viewportWidth && this.viewportWidth !== window.innerWidth && this.$refs.content) {
        await this.updateVariables()
        if (!this.itemsCentered && this.itemsWidth <= this.contentWidth) {
          this.$refs.content.style.display = 'flex'
          this.$refs.content.style.justifyContent = 'center'
          this.itemsCentered = true
          this.manager.get('pan').set({ enable: false })
        } else if (this.itemsCentered && this.itemsWidth > this.contentWidth) {
          this.$refs.content.style.display = ''
          this.$refs.content.style.justifyContent = ''
          this.itemsCentered = false
          this.manager.get('pan').set({ enable: true })
        }
        if (!this.freeMode) {
          await this.updateState()
        }
      }
    },
    blockEvents () {
      const events = [
        // 'onclick',
        'onmousedown',
        'onmousemove',
        'onmouseout',
        'onmouseover',
        'onmouseup',
        'ondblclick',
        'onfocus',
        'onblur'
      ]
      events.forEach((event) => {
        this.$refs.items[event] = () => {
          return false
        }
      })
    },
    async hammer () {
      if (process.client) {
        const Hammer = (await import('hammerjs')).default
        const Pan = new Hammer.Pan({
          pointers: 1,
          direction: Hammer.DIRECTION_HORIZONTAL
        })

        const Tap = new Hammer.Tap({
          taps: 1,
          threshold: 2
        })

        this.manager = new Hammer.Manager(this.$refs.content)

        this.manager.add(Pan)

        if (this.$slots.overlay) {
          this.overlayManager = new Hammer.Manager(this.$refs.overlayItem)
          this.overlayManager.add(Tap)
          this.overlayManager.on('tap', (ev) => {
            this.$emit('tap')
          })
        }

        this.manager.on('panstart', (e) => {
          if (this.freeMode) {
            return
          }
          this.panStart = true
        })
        this.manager.on('panmove', (e) => {
          if (this.animation) {
            return
          }
          if (this.freeMode) {
            return
          }
          const x = this.x + e.deltaX / 2
          this.$refs.items.style.transform = `translate(${x}px,0px)`
          this.$refs.items.style.webkitTransform = `translate(${x}px,0px)`
        })
        this.manager.on('panend', (e) => {
          if (this.animation) {
            return
          }
          if (this.freeMode) {
            // const x = this.x + e.deltaX
            // this.x = this.calculateXFreeMode(x)
            // this.setTranslate()
            // this.panStart = false

          } else {
            this.pan(e.deltaX)
          }
        })
        this.manager.on('pancancel', (e) => {
          const lastEvent = e.additionalEvent
          if (!this.freeMode) {
            this.panStart = true
            switch (lastEvent) {
              case 'panright':
                this.pan(10)
                break
              case 'panleft':
                this.pan(-10)
                break
              default:
                this.$refs.items.style.transform = `translate(${this.x}px,0px)`
                this.$refs.items.style.webkitTransform = `translate(${this.x}px,0px)`
                this.panStart = false
            }
          }
        })
      }
    },
    async pan (deltaX) {
      if (this.animation) {
        return
      }
      this.x = this.calculateX(this.firstVisibleItem, 'pan', deltaX)
      if (!this.showActive) {
        this.setActiveItem(
          this.contentItems[this.firstVisibleItem - 1].sort,
          true
        )
      }
      this.setActiveItemIndex(this.firstVisibleItem)
      await this.setTranslate()
      if (this.loopValue) {
        await this.shiftElements()
      }
      this.panStart = false
    },
    async setSlide (activeItemIndex, animation = true, emit = false) {
      this.x = this.calculateX(activeItemIndex, 'slide')
      await this.setTranslate(animation)
      this.setActiveItemIndex(activeItemIndex)
      // this.setActiveItem(this.contentItems[activeItemIndex - 1].sort, emit)
    },
    async setSlideFreeMode (n) {
      this.x = this.calculateXFreeMode(-1 * n * this.itemContainerWidth)
      await this.setTranslate()
    },
    setTranslate (animation = true) {
      return new Promise((resolve, reject) => {
        if (animation) {
          this.animation = true
          this.disableTouchScroll()
          if (this.$refs.items && this.$refs.items.style) {
            this.$refs.items.style.transition = 'transform .25s ease-in-out'
          }
        }
        if (this.$refs.items && this.$refs.items.style) {
          this.$refs.items.style.transform = `translate(${this.x}px,0px)`
          this.$refs.items.style.webkitTransform = `translate(${this.x}px,0px)`
        }

        if (animation) {
          setTimeout(() => {
            this.animation = false
            this.enableTouchScroll()
            if (this.$refs.items && this.$refs.items.style) {
              this.$refs.items.style.transition = ''
            }
            resolve()
          }, 260)
        } else {
          resolve()
        }
      })
    },
    calculateX (n, type, deltaX) {
      let tx = this.x
      let newFirstVisibleItem = this.firstVisibleItem
      // PAN
      // Liczenie nowego X dla przesunięcia (pan)
      // czyli ręcznego przesunięcia listy elementów
      // SLIDE
      // Ustawianie pierwszego widocznego elementu na podstawie numeru slajdu
      // Obliczenie przesunięcia dla karuzelki
      if (type === 'pan') {
        // Jeśli itemów jest mniej niż ma zostać wyświetlone
        // to kafelki nie są przesuwalne tylko wyświetlane od lewej strony
        if (this.countItems <= this.visibleItemsValue) {
          newFirstVisibleItem = 1
        } else {
          if (deltaX < 0) {
            if (
              this.firstVisibleItem + this.incrementValue <=
              this.countItems - this.visibleItemsValue + 1
            ) {
              newFirstVisibleItem = this.firstVisibleItem + this.incrementValue
            } else {
              newFirstVisibleItem = this.countItems - this.visibleItemsValue + 1
            }
          } else if (deltaX > 0) {
            if (this.firstVisibleItem - this.incrementValue >= 1) {
              newFirstVisibleItem = this.firstVisibleItem - this.incrementValue
            } else {
              newFirstVisibleItem = 1
            }
          }
          // Wyliczenie nowego X
          if (this.computedCoverflow) {
            const initialTx = this.itemContainerWidth * 0.1
            tx =
              -1 * ((newFirstVisibleItem - 1) * this.itemContainerWidth) -
                (newFirstVisibleItem - 2) * this.gapValue +
                initialTx
          } else {
            tx =
              -1 *
                (newFirstVisibleItem - 1) *
                (this.itemContainerWidth + this.gapValue)
          }
        }
      } else if (type === 'slide') {
        const initialTx = this.itemContainerWidth * 0.1
        // Jeśli itemów jest mniej niż ma zostać wyświetlone
        // to kafelki są wyświetlane od lewej strony
        if (this.countItems <= this.visibleItemsValue) {
          newFirstVisibleItem = 1
          tx =
            -1 *
              (newFirstVisibleItem - 1) *
              (this.itemContainerWidth + this.gapValue)
        } else if (this.computedCoverflow && this.loopValue) {
          newFirstVisibleItem = n
          tx =
            -1 * ((n - 1) * this.itemContainerWidth) -
              (n - 2) * this.gapValue +
              initialTx
        } else if (!this.computedCoverflow && this.loopValue) {
          newFirstVisibleItem = n
          tx =
            -1 *
              (newFirstVisibleItem - 1) *
              (this.itemContainerWidth + this.gapValue)
        } else {
          if (n <= this.middleItem) {
            newFirstVisibleItem = 1
          } else if (
            n > this.middleItem &&
            n < this.countItems - this.middleItem + 1
          ) {
            newFirstVisibleItem = n - this.middleItem + 1
          } else if (n >= this.countItems - this.middleItem + 1) {
            newFirstVisibleItem = this.countItems - this.visibleItemsValue + 1
          }
          if (this.computedCoverflow) {
            tx =
              -1 * ((newFirstVisibleItem - 1) * this.itemContainerWidth) -
                (newFirstVisibleItem - 2) * this.gapValue +
                initialTx
          } else {
            tx =
              -1 *
                (newFirstVisibleItem - 1) *
                (this.itemContainerWidth + this.gapValue)
          }
        }
      }
      this.firstVisibleItem = newFirstVisibleItem
      return tx
    },
    calculateXFreeMode (x) {
      if (x > 0) {
        x = 0
      } else if (
        this.itemsWidth > this.contentWidth &&
        x < -1 * (this.itemsWidth - this.contentWidth)
      ) {
        x = -1 * (this.itemsWidth - this.contentWidth)
      }
      return x
    },
    setScrollLeft (x) {
      this.$nextTick(() => {
        this.$refs.content.scrollTo(-1 * x, 0)
      })
    },
    setActiveItem (n, emit = false) {
      if (emit) {
        this.$emit('update', Number(n))
      }
      this.activeItem = Number(n)
    },
    setActiveItemIndex (n) {
      this.activeItemIndex = Number(n)
    },
    async shiftElements () {
      if (!this.hasPrevItem()) {
        const coverflowCorrection =
          this.computedCoverflow && this.activeItemIndex < 2 ? 1 : 0
        for (let i = coverflowCorrection; i >= 0; i--) {
          this.contentItems[this.contentItems.length - 1 - i].index = -1 - i
        }
        this.contentItems.sort((a, b) => a.index - b.index)
        this.contentItems.forEach((item, index) => {
          item.index = index
        })
        const newActiveItemIndex = this.computedCoverflow ? 3 : 2
        await this.setSlide(newActiveItemIndex, false)
      } else if (!this.hasNextItem()) {
        const coverflowCorrection = this.computedCoverflow
          ? this.visibleItemsValue
          : 0
        this.contentItems[0].index = this.contentItems.length
        this.contentItems.sort((a, b) => a.index - b.index)
        this.contentItems.forEach((item, index) => {
          item.index = index
        })
        await this.setSlide(
          this.contentItems.length -
          1 -
          (this.visibleItemsValue - 1) -
          coverflowCorrection,
          false
        )
      } else {
        await this.setSlide(this.activeItemIndex, this.showActive, false)
      }
    },
    async updateState (emit = false) {
      if (this.acceleration) {
        this.setActiveItem(this.contentItems[this.activeItemIndex - 1].sort)
        this.setScrollLeft(this.calculateX(this.activeItemIndex, 'slide'))
      } else if (this.loopValue) {
        await this.shiftElements()
      } else {
        await this.setSlide(this.activeItemIndex, this.showActive, emit)
      }
    },
    getCoverflowCenteredIndex (index) {
      return this.isMobile || this.isTablet
        ? index === this.activeItemIndex - 1
        : this.isDesktop && this.countItems >= 3
          ? index === this.activeItemIndex
          : true
    },
    getVisibleItemsByViewport () {
      let [mobile, tablet, desktop, fullhd] = [1, 1, 1, 1]
      if (this.isNumber(this.visibleItems)) {
        // Jeśli przekazana sama liczba -> ustaw na każdy viewport to samo
        mobile = this.visibleItems
        tablet = this.visibleItems
        desktop = this.visibleItems
        fullhd = this.visibleItems
      } else if (this.isObject(this.visibleItems)) {
        // Jeśli przekazany obiekt -> ustaw każdy z osobna lub poprzedni lub 1
        mobile = this.isNumber(this.visibleItems.mobile)
          ? this.visibleItems.mobile
          : 1
        tablet = this.isNumber(this.visibleItems.tablet)
          ? this.visibleItems.tablet
          : mobile
        desktop = this.isNumber(this.visibleItems.desktop)
          ? this.visibleItems.desktop
          : tablet
        fullhd = this.isNumber(this.visibleItems.fullhd)
          ? this.visibleItems.fullhd
          : desktop
      }
      return { mobile, tablet, desktop, fullhd }
    },
    getArrowsByViewport () {
      let [mobile, tablet, desktop, fullhd] = [false, false, false, false]
      if (this.isBoolean(this.arrows)) {
        // Jeśli przekazane true/false -> ustaw na każdy viewport to samo
        mobile = this.arrows
        tablet = this.arrows
        desktop = this.arrows
        fullhd = this.arrows
      } else if (this.isObject(this.arrows)) {
        // Jeśli przekazany obiekt -> ustaw każdy z osobna lub poprzedni lub false
        mobile = this.isBoolean(this.arrows.mobile) ? this.arrows.mobile : false
        tablet = this.isBoolean(this.arrows.tablet)
          ? this.arrows.tablet
          : mobile
        desktop = this.isBoolean(this.arrows.desktop)
          ? this.arrows.desktop
          : tablet
        fullhd = this.isBoolean(this.arrows.fullhd)
          ? this.arrows.fullhd
          : desktop
      }
      return { mobile, tablet, desktop, fullhd }
    },
    getCoverflowByViewport () {
      let [mobile, tablet, desktop, fullhd] = [false, false, false, false]
      if (this.isBoolean(this.coverflow)) {
        // Jeśli przekazane true/false -> ustaw na każdy viewport to samo
        mobile = this.coverflow
        tablet = this.coverflow
        desktop = this.coverflow
        fullhd = this.coverflow
      } else if (this.isObject(this.coverflow)) {
        // Jeśli przekazany obiekt -> ustaw każdy z osobna lub poprzedni lub false
        mobile = this.isBoolean(this.coverflow.mobile)
          ? this.coverflow.mobile
          : false
        tablet = this.isBoolean(this.coverflow.tablet)
          ? this.coverflow.tablet
          : mobile
        // Warunek do dopisania jeśli będzie potrzeba tego na desktopie
        // desktop = this.isBoolean(this.coverflow.desktop)
        //   ? this.coverflow.desktop
        //   : tablet
        desktop = false
        fullhd = false
      }
      return { mobile, tablet, desktop, fullhd }
    },
    handleClick (item) {
      if (this.showActive) {
        this.setActiveItem(item, true)
      } else if (this.zoom) {
        this.emitZoom()
      }
    },
    handleArrowClick (deltaX) {
      if (this.freeMode) {
        // TODO: napraw obsluge strzalek (glownie Werbe)
        // let n = 0
        // if (deltaX > 0) {
        //   n = this.getPrevActiveItemFreeMode()
        // } else if (deltaX < 0) {
        //   n = this.getNextActiveItemFreeMode()
        // }
        // this.setSlideFreeMode(n)

      } else {
        this.panStart = true
        this.pan(deltaX)
      }
    },
    emitZoom () {
      this.$emit('openModal', this.activeItem)
    },
    async updateVariables () {
      this.viewportWidth = window.innerWidth
      this.countItems = this.contentItems.length
      this.gapValue = this.gap
      this.contentWidth = this.$refs.content
        ? this.$refs.content.clientWidth
        : 0
      this.gapCssValue = `${this.gapValue}px`
      this.loopValue = this.acceleration ? false : this.loop

      if (!this.countItems) {
        this.itemContainerWidth = 0
        this.itemsWidth = 0
        return
      }

      if (this.freeMode) {
        await this.$nextTick(() => {
          if (this.$refs.contentItems && this.$refs.contentItems[0]) {
            const childElement =
              this.$refs.contentItems[0]?.children[0]?.children[0]?.children[0]
            if (childElement) {
              let width = window
                .getComputedStyle(childElement)
                .getPropertyValue('width')
              width = Number(width.slice(0, -2))
              if (this.isNumber(width)) {
                this.itemContainerWidth = width
                this.itemsWidth =
                  this.countItems * this.itemContainerWidth +
                    (this.countItems - 1) * this.gapValue
              }
            }
          }
        })
        this.contentWidth = this.$refs.content
          ? this.$refs.content.clientWidth
          : 0
      } else {
        await this.$nextTick(() => {
          this.contentWidth = this.$refs.content
            ? this.$refs.content.clientWidth
            : 0
          this.visibleItemsValue = this.computedVisibleItems
          if (this.computedCoverflow && !this.coverflowCentered) {
            const x = (this.contentWidth - 2 * this.gapValue) / 1.2
            const itemsWidth =
            this.countItems * x + (this.countItems - 1) * this.gapValue
            this.itemContainerWidth = x
            this.itemsWidth = itemsWidth
          } else if (this.computedCoverflow && this.coverflowCentered) {
            // this.gapValue = 0
            const x = (this.contentWidth - 2 * this.gapValue) / 1.2
            const itemsWidth =
            this.countItems * x + (this.countItems - 1) * this.gapValue
            this.itemContainerWidth = x
            this.itemsWidth = itemsWidth
          } else {
            const x =
            (this.contentWidth - (this.visibleItemsValue - 1) * this.gapValue) /
              this.visibleItemsValue
            const itemsWidth =
            this.countItems * x + (this.countItems - 1) * this.gapValue
            this.itemContainerWidth = x
            this.itemsWidth = itemsWidth
          }
          // Ustaw increment, jeśli nie przekazany = ilosc widocznych elementów
          this.incrementValue = this.increment
            ? this.increment
            : this.computedCoverflow
              ? 1
              : this.visibleItemsValue
        })
      }
    },
    // setStyles () {
    //   // Ustalenie wielkosci wyswietlonych elementów
    //   if (this.freeMode) {
    //     this.$nextTick(() => {
    //       const firstElement = this.$refs.contentItems[0]
    //       this.itemContainerWidth = firstElement.offsetWidth
    //       this.itemsWidth =
    //         this.countItems * this.itemContainerWidth +
    //         (this.countItems - 1) * this.gap
    //     })
    //   } else {
    //     this.itemContainerWidth =
    //       (this.contentWidth - (this.visibleItemsValue - 1) * this.gap) /
    //       this.visibleItemsValue
    //     if (this.computedCoverLeftRight) {
    //       this.itemContainerWidth =
    //         (this.contentWidth - (this.visibleItemsValue + 1) * this.gap) /
    //         (this.visibleItemsValue + 0.1)
    //     }
    //   }
    // },
    getActiveItem () {
      return this.activeItem
    },
    getPrevActiveItemFreeMode () {
      return Math.ceil(Math.abs(this.x) / this.itemContainerWidth) - 1
    },
    getNextActiveItemFreeMode () {
      return Math.floor(Math.abs(this.x) / this.itemContainerWidth) + 1
    },
    isActiveItem (index) {
      return this.visibleItemsValue > 1 && this.activeItem === index
    },
    isChildImage (child) {
      return (
        child &&
        child.item &&
        child.item.type &&
        child.item.type.name === 'CarouselItem'
      )
    },
    hasPrevItem () {
      const coverflowCorrection = this.computedCoverflow ? 1 : 0
      return this.activeItemIndex - coverflowCorrection > 1
    },
    hasNextItem () {
      const coverflowCorrection = this.computedCoverflow
        ? this.visibleItemsValue
        : 0
      return (
        this.activeItemIndex + coverflowCorrection + this.visibleItemsValue - 1 < this.countItems
      )
    },
    isObject (item) {
      return typeof item === 'object' && !Array.isArray(item) && item !== null
    },
    isNumber (item) {
      return typeof item === 'number'
    },
    isBoolean (item) {
      return typeof item === 'boolean'
    }
  }
}
</script>

<style lang="scss" scoped>
.carousel-container {
  position: relative;
  width: 100%;
  // max-height: fit-content;
  // @include mobile {
  //   width: calc(100% + 50px);
  //   margin: 0 -25px;
  // }
  .carousel-arrow-prev,
  .carousel-arrow-next {
    position: absolute;
    top: calc(50% - 20px);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: rgba(#eee, 0.5);
    z-index: 10;
    cursor: pointer;

    &:hover {
      background-color: rgba(#eee, 0.8);
    }

    svg {
      display: block;
      width: 20px;
      height: 20px;
    }
  }

  .carousel-arrow-prev {
    left: -5%;
      @media screen and (max-width: 1485px){
      left: -20px;
    }
      @include mobile{
    left: 0;
  }

    svg {
      margin-left: -3px;
    }
  }

  .carousel-arrow-next {
    right: -5%;
    @media screen and (max-width: 1485px){
      right: -20px;
    }
     @include mobile{
    right: 0;
    }

    svg {
      margin-right: -3px;
    }
  }

  .carousel-content {
    width: 100%;
    height: fit-content;
    position: relative;
    margin: 0 auto;
    max-width: 100%;
    overflow: hidden;
    overflow-x: var(--overflowValue);
    scroll-behavior: var(--scrollBehaviourValue);
    touch-action: pan-y;
    // @include touch{
    //   margin-left: -24px;
    //   margin-right: -24px;
    // }

    &.free-mode {
      touch-action: auto !important;
    }

    &.coverflow-centered {
      .carousel-item {
        scale: 0.9;
        transition: scale 0.3s ease-out;

        &.coverflow-centered {
          scale: 1;
        }
      }
    }

    &.smooth-scroll {
      scroll-behavior: smooth !important;
    }

    .carousel-items {
      width: auto;
      display: inline-flex;
      align-items: var(--alignItemsValue) !important;
      scroll-behavior: smooth;
      touch-action: auto !important;
      gap: var(--gapCssValue) !important;

      & > * {
        width: var(--itemContainerWidthValue);
        flex-shrink: 0;

        & > * {
          width: 100% !important;
        }
      }

      .carousel-item {
        cursor: pointer;
        align-self: start;

        &.active {
          opacity: 0.3;
        }
      }
    }

    & .overlay-item {
      height: fit-content;
      position: absolute;
      bottom: calc(3.5% + 7px);
      left: 50%;
      transform: translateX(-50%);
      font-size: 14px;
      }
   }

  .block-pointer-events {
    pointer-events: none;
  }
}
</style>
